import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Info, ShoppingCart, UserCog } from 'lucide-react'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { ReceiptListEntry } from '@/api/warehouse'

// Das Popover öffnet erst nach bewusstem Verweilen über dem Inhalt.
const HOVER_DELAY = 600
const fmt = (n: number) => n.toLocaleString('de-DE')
type Tab = 'offen' | 'vollstaendig' | 'abgeschlossen' | 'alle'
const positionLabel = (count: number) => (count === 1 ? 'Position' : 'Positionen')
const paletteLabel = (count: number) => (count === 1 ? 'Palette' : 'Paletten')
const dateTime = (value: string) =>
  new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

function useWideLayout() {
  const [wide, setWide] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 900px)').matches,
  )
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(min-width: 900px)')
    const update = () => setWide(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  return wide
}
interface StatusStyle {
  label: string
  className: string
  bar: string
}

const statusTable: Record<string, StatusStyle> = {
  offen: { label: 'Offen', className: 'bg-primary-soft text-primary', bar: 'bg-primary' },
  teilweise: { label: 'Teilweise', className: 'bg-amber-100 text-amber-800', bar: 'bg-amber-500' },
  vollstaendig: {
    label: 'Vollständig',
    className: 'bg-green-100 text-green-800',
    bar: 'bg-green-600',
  },
  abgeschlossen: {
    label: 'Abgeschlossen',
    className: 'bg-gray-100 text-gray-700',
    bar: 'bg-gray-400',
  },
  storniert: { label: 'Storniert', className: 'bg-gray-100 text-gray-700', bar: 'bg-gray-400' },
  eskaliert: { label: 'Eskaliert', className: 'bg-red-100 text-red-800', bar: 'bg-red-600' },
}
const statusStyle = (status: string) =>
  statusTable[status] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-700',
    bar: 'bg-gray-400',
  }
export function effektiverStatus(r: ReceiptListEntry): string {
  return r.status === 'abgeschlossen' ? 'abgeschlossen' : r.progress.status
}
function Status({ r }: { r: ReceiptListEntry }) {
  const status = effektiverStatus(r)
  const escalated =
    r.escalation.level === 'eskaliert' && status !== 'abgeschlossen' && status !== 'vollstaendig'
  const cls = statusStyle(escalated ? 'eskaliert' : status).className
  const incomplete = r.items.filter(
    (i) => i.status !== 'vollstaendig' && i.status !== 'storniert',
  ).length
  const hint =
    incomplete && status === 'teilweise'
      ? `${incomplete} Position(en) nicht vollständig – Soll/Ist im Auftrag prüfen`
      : undefined
  return (
    <span title={hint} className={`inline-block rounded px-2 py-1 text-xs ${cls}`}>
      {escalated ? `Eskaliert · ${r.escalation.daysOpen} Tage` : statusStyle(status).label}
    </span>
  )
}
function Content({ r }: { r: ReceiptListEntry }) {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const active = r.items.filter((i) => i.status !== 'storniert')
  const qty = active.reduce((n, i) => n + i.totalQty, 0)
  const cancelled = r.items.length - active.length
  const done = active.filter((i) => i.status === 'vollstaendig').length
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )
  const clearHoverTimer = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }
  const leave = () => {
    clearHoverTimer()
    if (!pinned) timer.current = setTimeout(() => setOpen(false), 120)
  }
  return (
    <div
      onMouseEnter={() => {
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => setOpen(true), HOVER_DELAY)
      }}
      onMouseLeave={leave}
      onClick={(e) => e.stopPropagation()}
      className="min-w-0"
    >
      <div className="flex items-center gap-2">
        <div className={active.length === 0 && cancelled ? 'text-muted' : undefined}>
          {active.length === 0 && cancelled
            ? `0 aktive Positionen · ${cancelled} storniert`
            : `${active.length} ${positionLabel(active.length)} · ${fmt(qty)} Stück${cancelled ? ` · ${cancelled} storniert` : ''}`}
        </div>
        <Popover
          open={open}
          onOpenChange={(v) => {
            clearHoverTimer()
            setOpen(v)
            setPinned(v)
          }}
        >
          <PopoverTrigger asChild>
            <button
              aria-label="Positionen anzeigen"
              className="rounded p-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                clearHoverTimer()
                setPinned(!pinned)
                setOpen(!pinned)
              }}
            >
              <Info size={17} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[min(520px,90vw)]"
            onMouseEnter={() => {
              if (timer.current) clearTimeout(timer.current)
            }}
            onMouseLeave={leave}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between gap-2">
              <strong>
                #{r.id} · {r.herkunft.bestellung?.lieferant ?? 'Ohne Bestellung'}
                {(r.herkunft.bestellung?.orderNumber ?? r.orderNo) &&
                  ` · ${r.herkunft.bestellung?.orderNumber ?? r.orderNo}`}
              </strong>
              <Status r={r} />
            </div>
            <div className="grid grid-cols-3 gap-2 py-3">
              <div>
                <div className="text-xs text-muted">{positionLabel(active.length)} fertig</div>
                <div className="text-lg font-semibold">
                  {done}/{active.length}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted">
                  {paletteLabel(r.progress.expected)} gebucht
                </div>
                <div className="text-lg font-semibold">
                  {r.progress.booked}/{r.progress.expected}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted">Stück gesamt</div>
                <div className="text-lg font-semibold">{fmt(qty)}</div>
              </div>
            </div>
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr>
                    <th>Artikel</th>
                    <th>Menge</th>
                    <th>Kartons</th>
                    <th>Etiketten</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {r.items.map((i) => (
                    <tr
                      className={`border-t border-line ${i.status === 'storniert' ? 'text-muted line-through' : ''}`}
                      key={i.id}
                    >
                      <td className="py-2">
                        <GrundartikelLink code={i.merchantSku} />
                        <div className="max-w-40 truncate">{i.itemName}</div>
                      </td>
                      <td>{fmt(i.totalQty)}</td>
                      <td>{i.cartonsDelivered ?? '–'}</td>
                      <td>
                        {i.etikettenGebucht}/{i.etikettenGesamt}
                        <div className="h-1 w-14 rounded bg-gray-200">
                          <div
                            className="h-1 rounded bg-primary"
                            style={{
                              width: `${i.etikettenGesamt ? Math.min(100, (i.etikettenGebucht / i.etikettenGesamt) * 100) : 0}%`,
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <span className={`rounded px-1 py-0.5 ${statusStyle(i.status).className}`}>
                          {statusStyle(i.status).label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Link
              className="mt-3 inline-block text-primary underline"
              to={`/warehouse/wareneingang/${r.id}`}
            >
              Auftrag öffnen →
            </Link>
          </PopoverContent>
        </Popover>
      </div>
      <div className="max-w-64 truncate text-xs text-muted">
        {active.slice(0, 3).map((i, idx) => (
          <span key={i.id}>
            {idx > 0 && ' · '}
            <GrundartikelLink code={i.merchantSku} />
          </span>
        ))}{' '}
        {active.length > 3 && `+${active.length - 3}`}
      </div>
    </div>
  )
}
function Origin({ r }: { r: ReceiptListEntry }) {
  const h = r.herkunft
  const Icon = h.quelle === 'einkauf' ? ShoppingCart : UserCog
  return (
    <div>
      <div>
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${h.quelle === 'einkauf' ? 'bg-primary-soft text-primary' : 'bg-gray-100 text-gray-700'}`}
        >
          <Icon size={13} />
          {h.quelle === 'einkauf'
            ? 'Einkauf'
            : `Leiter${r.createdByName ? ` · ${r.createdByName}` : ''}`}
        </span>{' '}
        {r.mode === 'erstbetriebnahme' && <span className="text-xs">Erstbetrieb</span>}
      </div>
      <div className={h.bestellung?.lieferant ? 'font-semibold' : 'text-muted'}>
        {h.bestellung?.lieferant ?? 'Ohne Bestellung'}
      </div>
      <div className="text-xs text-muted">
        {h.bestellung?.orderNumber ?? r.orderNo ?? '–'}
        {h.container && (
          <>
            {' '}
            · <span>{h.container.label ?? h.container.containerNo}</span>
            {h.container.label && h.container.containerNo && ` · ${h.container.containerNo}`}
          </>
        )}
      </div>
    </div>
  )
}
function Progress({ r }: { r: ReceiptListEntry }) {
  const active = r.items.filter((i) => i.status !== 'storniert')
  const done = active.filter((i) => i.status === 'vollstaendig').length
  const p = r.progress
  const color = statusStyle(
    r.escalation.level === 'eskaliert' ? 'eskaliert' : effektiverStatus(r),
  ).bar
  return (
    <div className="min-w-28">
      <div className="h-1.5 rounded bg-gray-200">
        <div
          className={`h-1.5 rounded ${color}`}
          style={{ width: `${p.expected ? Math.min(100, (p.booked / p.expected) * 100) : 0}%` }}
        />
      </div>
      <div>
        {p.booked} / {p.expected} {paletteLabel(p.expected)}
      </div>
      <div className="text-xs text-muted">
        {done} / {active.length} {positionLabel(active.length)} fertig
      </div>
    </div>
  )
}
export function ReceiptOverview({ rows, loading }: { rows: ReceiptListEntry[]; loading: boolean }) {
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const v = localStorage.getItem('we-receipt-tab')
      if (v === 'vollstaendig' || v === 'abgeschlossen' || v === 'alle') return v
    } catch {}
    return 'offen'
  })
  const [origin, setOrigin] = useState('alle')
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const wide = useWideLayout()
  const status = effektiverStatus
  const counts = {
    offen: rows.filter((r) => ['offen', 'teilweise'].includes(status(r))).length,
    vollstaendig: rows.filter((r) => status(r) === 'vollstaendig').length,
    abgeschlossen: rows.filter((r) => status(r) === 'abgeschlossen').length,
    alle: rows.length,
  }
  const list = rows.filter(
    (r) =>
      (tab === 'alle' ||
        (tab === 'offen' ? ['offen', 'teilweise'].includes(status(r)) : status(r) === tab)) &&
      (origin === 'alle' || r.herkunft.quelle === origin) &&
      [
        String(r.id),
        r.orderNo,
        r.herkunft.bestellung?.orderNumber,
        r.herkunft.bestellung?.lieferant,
        ...r.items.flatMap((i) => [i.merchantSku, i.itemName]),
      ].some((x) => x?.toLocaleLowerCase().includes(query.toLocaleLowerCase())),
  )
  const tabs: Array<[Tab, string]> = [
    ['offen', 'Offen'],
    ['vollstaendig', 'Vollständig'],
    ['abgeschlossen', 'Abgeschlossen'],
    ['alle', 'Alle'],
  ]
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex flex-wrap gap-1">
          {tabs.map(([v, label]) => (
            <button
              key={v}
              className={`rounded px-2 py-1 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${tab === v ? 'bg-primary-soft text-primary font-semibold' : 'text-muted'}`}
              onClick={() => {
                setTab(v)
                try {
                  localStorage.setItem('we-receipt-tab', v)
                } catch {}
              }}
            >
              {label} ({counts[v]})
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {[
            ['alle', 'Alle'],
            ['einkauf', 'Einkauf'],
            ['leiter', 'Leiter'],
          ].map(([v, label]) => (
            <button
              key={v}
              className={`rounded px-2 py-1 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${origin === v ? 'bg-primary-soft text-primary' : 'text-muted'}`}
              onClick={() => setOrigin(v!)}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          className="rounded border border-line bg-panel px-2 py-1 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          placeholder="Suchen: Nr., Bestellung, Lieferant, Artikel"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {wide ? (
        <div className="rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr>
                {['Nr.', 'Herkunft', 'Inhalt', 'Fortschritt', 'Status', 'Angelegt'].map((x) => (
                  <th className="p-2" key={x}>
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-line hover:bg-primary-soft cursor-pointer"
                  onClick={() => navigate(`/warehouse/wareneingang/${r.id}`)}
                >
                  <td className="p-2">
                    <Link className="text-primary underline" to={`/warehouse/wareneingang/${r.id}`}>
                      #{r.id}
                    </Link>
                  </td>
                  <td className="p-2">
                    <Origin r={r} />
                  </td>
                  <td className="p-2">
                    <Content r={r} />
                  </td>
                  <td className="p-2">
                    <Progress r={r} />
                  </td>
                  <td className="p-2">
                    <Status r={r} />
                  </td>
                  <td className="p-2 text-xs">{dateTime(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border border-line bg-panel p-3 space-y-2"
              onClick={() => navigate(`/warehouse/wareneingang/${r.id}`)}
            >
              <div className="flex justify-between gap-2">
                <Link to={`/warehouse/wareneingang/${r.id}`} className="text-primary underline">
                  #{r.id}
                </Link>
                <Origin r={r} />
                <Status r={r} />
              </div>
              <Content r={r} />
              <Progress r={r} />
              <div className="text-xs text-muted">{dateTime(r.createdAt)}</div>
            </div>
          ))}
        </div>
      )}
      {!list.length && !loading && (
        <p className="p-4 text-center text-muted">
          {tab === 'offen' && origin === 'alle' && !query
            ? 'Keine offenen WE-Aufträge – alles eingelagert.'
            : 'Keine WE-Aufträge für diese Auswahl.'}
        </p>
      )}
    </section>
  )
}
